/**
 * «Расширение Chrome»: помощник установки для Windows (вкладка по умолчанию), скачать последнюю
 * сборку, как поставить и обновить вручную (вкладки `?tab=`),
 * стоит ли расширение в этом браузере и у кого оно сейчас работает — на каких площадках и с каких
 * номеров (имя номера, рабочий или личный — справочник номеров CRM).
 * Спека `docs/specs/2026-10-03-extension-page-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Download } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

import shotCard from "@/assets/ext/card.png";
import marks from "@/assets/ext/marks.json";
import shotDevMode from "@/assets/ext/dev-mode.png";
import shotHelperLogin from "@/assets/ext/helper-login.png";
import shotHelperOk from "@/assets/ext/helper-ok.png";
import shotHelperUpdate from "@/assets/ext/helper-update.png";
import shotHelperWindow from "@/assets/ext/helper-window.png";
import shotChat from "@/assets/ext/how-chat.jpg";
import shotDress from "@/assets/ext/how-dress.jpg";
import shotFilter from "@/assets/ext/how-filter.jpg";
import shotMyBooked from "@/assets/ext/how-my-booked.jpg";
import shotMyCity from "@/assets/ext/how-my-city.jpg";
import shotPanel from "@/assets/ext/how-panel.jpg";
import shotField from "@/assets/ext/how-zapis-field.jpg";
import shotLoad from "@/assets/ext/load-unpacked.png";
import {
  extHelperCommand,
  extHelperFile,
  loadExtBugs,
  loadExtHelperEvents,
  loadExtStatus,
  setExtPhoneChannel,
  type ExtBug,
  type ExtBugEvent,
  type ExtHelper,
  type ExtHelperBrowser,
  type ExtInstall,
  type ExtStatus,
  type ExtTab,
} from "@/lib/extension";
import { ChannelCell, Dot, ago, when } from "@/components/ext/PhoneChannel";
import { EXT_JOURNAL, useCan } from "@/lib/access";
import { BridgeJournal } from "@/pages/ExtensionBridge";
import { Journal } from "@/pages/ExtensionJournal";

/** Что расширение написало о себе в `<html>` этой страницы (`extension/here.js`). */
type Here = { version: string; install: string };

function useHere(): Here | null {
  const read = (): Here | null => {
    const d = document.documentElement.dataset;
    return d["prokatExt"] ? { version: d["prokatExt"], install: d["prokatExtInstall"] ?? "" } : null;
  };
  const [here, setHere] = useState<Here | null>(read);
  useEffect(() => {
    // Id установки расширение дописывает чуть позже версии — следим за атрибутами.
    const watch = new MutationObserver(() => setHere(read()));
    watch.observe(document.documentElement, { attributes: true, attributeFilter: ["data-prokat-ext", "data-prokat-ext-install"] });
    return () => watch.disconnect();
  }, []);
  return here;
}

const phone = (p: string) =>
  /^7\d{10}$/.test(p) ? `+7 ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9)}` : p ? `+${p}` : "";

const size = (bytes: number | null) => (bytes ? `${Math.round(bytes / 1024)} КБ` : "");
/** Значок расширения, как на панели Chrome: цвет — свежесть версии, цифры — её номер. */
function Badge({ tone, version }: { tone: "ok" | "err"; version: string }) {
  const color = tone === "ok" ? "#1a9c4b" : "#d93025";
  return (
    <span className="relative inline-block size-8 flex-none" aria-hidden>
      <span className="absolute inset-0 grid place-items-center rounded-[7px] pb-2 text-[15px] font-bold leading-none text-white" style={{ background: color }}>
        П
      </span>
      <span className="absolute -bottom-0.5 -right-1 rounded-[3px] px-[3px] text-[10px] font-semibold leading-[13px] text-white" style={{ background: color, outline: "1.5px solid var(--bg)" }}>
        {version}
      </span>
    </span>
  );
}

const KIND = { work: "рабочий", personal: "личный", unknown: "нет в справочнике номеров" } as const;

function TabChip({ tab }: { tab: ExtTab }) {
  const tone = !tab.active ? "border-border text-fg-faint" : tab.kind === "work" ? "border-ok/40 bg-ok-bg" : "border-border bg-bg-subtle";
  return (
    <div className={`flex flex-wrap items-baseline gap-x-2 rounded-[var(--radius-ui)] border px-2.5 py-1.5 text-[12.5px] ${tone}`} data-annot={`вкладка ${tab.platform}`}>
      <span className="font-semibold">{tab.platform}</span>
      {tab.phone ? (
        <>
          <span>{tab.name ? `«${tab.name}»` : "без названия"}</span>
          <span className="tabular-nums">{phone(tab.phone)}</span>
          <span className={tab.kind === "unknown" ? "text-warn" : "text-fg-muted"}>
            {KIND[tab.kind]}
            {tab.kind === "work" && tab.group ? ` · ${tab.group}` : ""}
          </span>
        </>
      ) : (
        <span className="text-fg-muted">аккаунт пока не определён — откройте эту вкладку (в фоне площадка могла не загрузиться или в неё не вошли)</span>
      )}
      <span className="text-fg-faint">{tab.active ? (tab.visible ? "вкладка на виду" : "вкладка в фоне") : `вкладку закрыли · ${ago(tab.age)}`}</span>
    </div>
  );
}

function InstallRow({ install, latest, here, gone }: { install: ExtInstall; latest: string | null; here: Here | null; gone?: boolean }) {
  const active = install.tabs.filter((t) => t.active);
  const closed = install.tabs.filter((t) => !t.active);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[12.5px] text-fg-muted">
        <Dot tone={install.active ? "ok" : "off"} />
        <span className={install.active ? "font-medium text-fg" : ""}>{install.active ? "активно" : `неактивно · ${ago(install.age)}`}</span>
        <span>{install.browser || "браузер"}</span>
        {gone ? (
          <span>версия {install.version} — расширение не отмечается больше недели: в этом браузере им не пользуются</span>
        ) : (
          <span className={install.fresh ? "text-ok" : "text-err"}>
            версия {install.version}
            {install.fresh ? "" : latest ? ` — пора обновить до ${latest}` : ""}
          </span>
        )}
        {here?.install === install.install && <span className="rounded-[4px] bg-accent-dim px-1.5 text-accent-text">этот браузер</span>}
      </div>
      {install.active && active.length === 0 && (
        <div className="pl-4 text-[12.5px] text-fg-faint">Вкладки Telegram, MAX и WhatsApp не открыты.</div>
      )}
      {active.length > 0 && (
        <div className="flex flex-col gap-1 pl-4">
          {active.map((t) => (
            <TabChip key={t.channel + t.account_id} tab={t} />
          ))}
        </div>
      )}
      {closed.length > 0 && (
        <div className="flex flex-col gap-1 pl-4">
          {closed.map((t) => (
            <TabChip key={t.channel + t.account_id} tab={t} />
          ))}
        </div>
      )}
    </div>
  );
}

const BROWSER: Record<ExtHelperBrowser["status"], string> = {
  none: "не установлено",
  installed: "стоит",
  disabled: "отключено в браузере",
  old: "старая папка",
};

/** Компьютер с помощником установки: что в общей папке и в каких браузерах расширение стоит. */
function HelperRow({ helper, latest }: { helper: ExtHelper; latest: string | null }) {
  return (
    <div className="flex flex-col gap-1.5" data-annot={`компьютер ${helper.computer}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 text-[12.5px] text-fg-muted">
        <Dot tone={helper.online ? "ok" : "off"} />
        <span className="font-medium text-fg">Компьютер {helper.computer || "без имени"}</span>
        <span className={helper.online ? "text-ok" : ""}>
          помощник{helper.version ? ` ${helper.version}` : ""} {helper.online ? "на связи" : `не на связи · был ${ago(helper.age)}`}
        </span>
        <span className={helper.fresh ? "text-ok" : "text-err"}>
          {helper.folder_version ? `в папке ${helper.folder_version}` : "расширение ещё не скачано"}
          {helper.folder_version && !helper.fresh && latest ? ` — обновится до ${latest} само` : ""}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 pl-4">
        {helper.browsers.map((b) => {
          const good = b.status === "installed" && b.fresh;
          const tone = b.status === "none" ? "border-border text-fg-faint" : good ? "border-ok/40 bg-ok-bg text-ok" : "border-err/40 bg-err-bg text-err";
          return (
            <span key={b.name + b.folder + b.profile} className={`rounded-[4px] border px-1.5 py-0.5 text-[12px] ${tone}`}>
              <span className="font-medium">{b.name || b.folder}</span>
              {b.profile ? ` · ${b.profile}` : ""} — {BROWSER[b.status]}
              {b.version ? ` · ${b.version}` : ""}
            </span>
          );
        })}
        {helper.browsers.length === 0 && <span className="text-[12.5px] text-fg-faint">Браузеры Chrome не найдены.</span>}
      </div>
    </div>
  );
}

function Who({ status, here }: { status: ExtStatus; here: Here | null }) {
  const latest = status.release?.version ?? null;
  if (status.users.length === 0)
    return <div className="text-[13px] text-fg-muted">Расширение пока ни у кого не отметилось. Поставьте его по инструкции на вкладке «Помощник» и откройте Telegram, MAX или WhatsApp Web.</div>;
  return (
    <div className="flex flex-col divide-y divide-border">
      {status.users.map((u) => (
        <div key={u.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0" data-annot={`сотрудник ${u.name}`}>
          <div className="flex items-baseline gap-2 text-[14px] font-semibold">
            {u.name || `#${u.id}`}
            {u.me && <span className="text-[12px] font-normal text-fg-faint">это вы</span>}
          </div>
          {u.installs.map((i) => (
            <InstallRow key={i.install} install={i} latest={latest} here={here} gone={u.gone} />
          ))}
          {u.hidden > 0 && (
            <div className="pl-4 text-[12px] text-fg-faint">
              прежних установок не показано: {u.hidden} — обновлены в том же браузере или молчат больше недели
            </div>
          )}
          {u.helpers.map((h) => (
            <HelperRow key={h.id} helper={h} latest={latest} />
          ))}
          {u.helpers.length === 0 && (
            <div className="flex items-baseline gap-2 text-[12.5px] text-err">
              <Dot tone="err" />
              помощника нет — расширение обновляет вручную
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const GROUP = "Клиенты"; // группа справочника по умолчанию — номера КЦ для клиентов

/**
 * Разрез по номерам (владелец, 06.10.26): рабочий номер × WhatsApp / Telegram / MAX — кто на канале
 * сейчас и кто был последним; «Не онлайн» оставляет номера, где хоть один канал никто не держит.
 */
function Phones({ status, group, off, can, set }: { status: ExtStatus; group: string; off: boolean; can: boolean; set: (patch: Record<string, string>) => void }) {
  const groups = [...new Set(status.phones.map((p) => p.group))];
  const current = groups.includes(group) ? group : groups.includes(GROUP) ? GROUP : (groups[0] ?? "");
  const rows = status.phones.filter((p) => p.group === current);
  // Канал, который не используем (номер на площадке заблокирован), — не канал и не «не онлайн».
  const channels = rows.flatMap((p) => p.channels).filter((c) => !c.off);
  const down = channels.filter((c) => !c.active).length;
  const shown = off ? rows.filter((p) => p.channels.some((c) => !c.active && !c.off)) : rows;
  const chip = (on: boolean) =>
    `rounded-[6px] px-3 py-1.5 text-[13px] font-medium ${on ? "bg-accent-dim text-accent-text" : "text-fg-muted hover:bg-bg-hover hover:text-fg"}`;
  if (status.phones.length === 0)
    return <div className="text-[13px] text-fg-muted">Справочник номеров CRM сейчас недоступен — разрез по номерам собрать не из чего.</div>;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div role="tablist" aria-label="Какие каналы" className="flex w-fit gap-1 rounded-[var(--radius-ui)] border border-border p-1" data-annot="фильтр каналов">
          <button type="button" role="tab" aria-selected={!off} onClick={() => set({ off: "" })} className={chip(!off)}>
            Все · {channels.length}
          </button>
          <button type="button" role="tab" aria-selected={off} onClick={() => set({ off: "1" })} className={chip(off)}>
            Не онлайн · {down}
          </button>
        </div>
        <div role="tablist" aria-label="Группа номеров" className="flex w-fit flex-wrap gap-1 rounded-[var(--radius-ui)] border border-border p-1" data-annot="группа номеров">
          {groups.map((g) => (
            <button key={g} type="button" role="tab" aria-selected={g === current} onClick={() => set({ group: g === GROUP ? "" : g })} className={chip(g === current)}>
              {g || "без группы"}
            </button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="text-[13px] text-ok">Все каналы группы онлайн.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-auto text-[12.5px]" data-annot="номера и каналы">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="py-1.5 pr-6 font-medium">Номер</th>
                {shown[0]?.channels.map((c) => (
                  <th key={c.channel} className="py-1.5 pr-6 font-medium">
                    {c.platform}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shown.map((p) => (
                <tr key={p.phone} className="align-top" data-annot={`номер ${p.name}`}>
                  <td className="whitespace-nowrap py-2 pr-6">
                    <div className="text-[13.5px] font-semibold">{p.name || "без названия"}</div>
                    <div className="tabular-nums text-fg-muted">{phone(p.phone)}</div>
                  </td>
                  {p.channels.map((c) => (
                    <td key={c.channel} className={`min-w-[190px] py-2 pr-6 ${off && (c.active || c.off) ? "opacity-40" : ""}`}>
                      <ChannelCell c={c} toggle={can ? (next) => setExtPhoneChannel(p.phone, c.channel, next) : undefined} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {status.phones_unknown.length > 0 && (
        <div className="text-[12.5px] text-fg-muted" data-annot="вкладки без номера">
          Аккаунт открыт, но площадка не отдала его номер — к строке номера не отнести:{" "}
          {status.phones_unknown.map((t) => `${t.platform} ${t.account_id} — ${t.name}, ${t.active ? "сейчас" : ago(t.age)}`).join("; ")}
        </div>
      )}
    </div>
  );
}

/**
 * Связь с помощником одной строкой: у кого он стоит и на связи (обновления доходят сами, просить
 * никого не нужно), у кого стоит, но молчит, и у кого его нет.
 */
function Helpers({ status }: { status: ExtStatus }) {
  if (status.users.length === 0) return null;
  const name = (u: ExtStatus["users"][number]) => u.name || `#${u.id}`;
  const online = status.users.filter((u) => u.helpers.some((h) => h.online));
  const silent = status.users.filter((u) => u.helpers.length > 0 && !u.helpers.some((h) => h.online));
  const none = status.users.filter((u) => u.helpers.length === 0);
  const tone = silent.length + none.length ? "border-err/40 bg-err-bg" : "border-ok/40 bg-ok-bg";
  return (
    <div className={`mb-3 flex flex-col gap-1 rounded-[var(--radius-ui)] border px-3 py-2 text-[13px] ${tone}`} data-annot="связь с помощником">
      <span className="font-semibold">
        Помощник на связи: {online.length} из {status.users.length}
        <span className="font-normal text-fg-muted"> — им новая версия приходит сама, в течение минуты</span>
      </span>
      {online.length > 0 && <span className="text-ok">на связи: {online.map(name).join(", ")}</span>}
      {silent.length > 0 && (
        <span className="text-err">
          стоит, но не на связи (компьютер выключен или помощник не запущен):{" "}
          {silent.map((u) => `${name(u)} — был ${ago(Math.min(...u.helpers.map((h) => h.age)))}`).join("; ")}
        </span>
      )}
      {none.length > 0 && <span className="text-err">помощника нет: {none.map(name).join(", ")}</span>}
    </div>
  );
}

/**
 * Контроль версий одной строкой: сколько сотрудников на последней сборке и кому пора обновить.
 * Судим по установкам, работающим сейчас; ни одна не активна — по последней отметившейся.
 * Расширения не видно неделю — им не пользуются: это не «пора обновить», а отдельная строка.
 */
function Versions({ status }: { status: ExtStatus }) {
  const latest = status.release?.version;
  if (!latest || status.users.length === 0) return null;
  const users = status.users.filter((u) => !u.gone);
  const gone = status.users.filter((u) => u.gone);
  const stale = users.flatMap((u) => {
    const live = u.installs.filter((i) => i.active);
    const old = (live.length ? live : u.installs.slice(0, 1)).find((i) => !i.fresh);
    return old ? [{ id: u.id, name: u.name || `#${u.id}`, version: old.version, active: old.active, age: old.age }] : [];
  });
  const tone = stale.length ? "border-err/40 bg-err-bg" : "border-ok/40 bg-ok-bg";
  return (
    <div className={`mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-[var(--radius-ui)] border px-3 py-2 text-[13px] ${tone}`} data-annot="версии у сотрудников">
      <span className="font-semibold">
        На последней версии {latest}: {users.length - stale.length} из {users.length}
      </span>
      {stale.length > 0 && (
        <span className="text-err">
          пора обновить:{" "}
          {stale.map((s) => `${s.name} — ${s.version}${s.active ? ", работает сейчас" : `, был(а) ${ago(s.age)}`}`).join("; ")}
        </span>
      )}
      {gone.length > 0 && (
        <span className="basis-full text-fg-muted">
          расширения не видно больше недели — в браузере его нет или им не пользуются:{" "}
          {gone.map((u) => `${u.name || `#${u.id}`} — был(а) ${ago(u.installs[0]?.age ?? 0)}`).join("; ")}
        </span>
      )}
    </div>
  );
}

type Mark = { x: number; y: number; w: number; h: number };

/** Скриншот шага с рамкой вокруг того, что нажимать: координаты рамки — в процентах картинки. */
function Shot({ src, alt, mark, width }: { src: string; alt: string; mark?: Mark | Mark[]; width?: number }) {
  const marks = mark ? (Array.isArray(mark) ? mark : [mark]) : [];
  return (
    <div className="relative mt-2 w-fit max-w-full overflow-hidden rounded-[var(--radius-ui)] border border-border">
      <img src={src} alt={alt} width={width} className="block max-w-full" />
      {marks.map((m, i) => (
        <span
          key={i}
          className="pointer-events-none absolute rounded-[6px] border-2 border-[#ff5a36] shadow-[0_0_0_3px_rgba(255,90,54,.25)]"
          style={{ left: `${m.x}%`, top: `${m.y}%`, width: `${m.w}%`, height: `${m.h}%` }}
        />
      ))}
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="grid size-6 flex-none place-items-center rounded-full bg-fg text-[12.5px] font-semibold text-bg">{n}</span>
      <div className="min-w-0 text-[13px] text-fg-muted">
        <div className="text-[14px] font-medium text-fg">{title}</div>
        {children}
      </div>
    </li>
  );
}

const code = "rounded-[4px] bg-bg-subtle px-1.5 py-0.5 font-mono text-[12px] text-fg";
const FOLDER = "C:\\prokat-ext";

function DownloadButton({ status }: { status: ExtStatus }) {
  const r = status.release;
  if (!r?.version) return <span className="text-[13px] text-fg-muted">Сборка ещё не выпущена.</span>;
  return (
    <a
      href="/api/ext/download"
      download
      className="inline-flex w-fit items-center gap-2 rounded-[var(--radius-ui)] bg-accent px-3.5 py-2 text-[13.5px] font-semibold text-white hover:opacity-90"
      data-annot="скачать расширение"
    >
      <Download size={15} />
      Скачать prokat-ext.zip · версия {r.version}
    </a>
  );
}

/** Короткая отсылка к помощнику — в начале ручных инструкций. */
function HelperCallout({ go }: { go: () => void }) {
  return (
    <div className="flex max-w-[860px] flex-wrap items-center gap-x-3 gap-y-1 rounded-[var(--radius-card)] border border-accent/40 bg-accent-dim/40 px-4 py-3 text-[13px]" data-annot="отсылка к помощнику">
      <span className="font-semibold">На Windows проще через помощника:</span>
      <span className="text-fg-muted">он сам находит ваши браузеры Chrome и дальше обновляет расширение без вас.</span>
      <button type="button" onClick={go} className="font-semibold text-accent-text underline underline-offset-2">
        Открыть вкладку «Помощник»
      </button>
    </div>
  );
}

const HELPER_CODE: Record<string, string> = {
  install: "установка",
  shortcut: "ярлык",
  login: "вход",
  logout: "выход",
  task: "автозапуск",
  agent: "фоновый помощник",
  ext_update: "расширение в папке",
  self_update: "сам помощник",
  sync: "сверка",
  browser: "браузер",
  put: "кнопка «Поставить»",
  ui: "окно",
  remove: "удаление",
  error: "ошибка",
};

/** Журнал помощника: что он сделал на компьютерах сотрудников и что не получилось. */
function HelperJournal() {
  const [errors, setErrors] = useState(false);
  const q = useQuery({ queryKey: ["ext-helper-events", errors], queryFn: () => loadExtHelperEvents(errors), refetchInterval: 30_000 });
  const events = q.data?.events ?? [];
  return (
    <section className="flex flex-col gap-3" data-annot="журнал помощника">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-[14px] font-semibold">Журнал помощника</h3>
        <span className="text-[12px] text-fg-faint">
          все сотрудники · что помощник сделал на компьютере, что обновил и что не получилось
        </span>
        <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={errors} onChange={(e) => setErrors(e.target.checked)} />
          только то, что не получилось
        </label>
      </div>
      {q.isError ? (
        <div className="text-[13px] text-err">{q.error.message}</div>
      ) : events.length === 0 ? (
        <div className="text-[13px] text-fg-muted">{q.isLoading ? "Загрузка…" : errors ? "Ошибок нет." : "Событий пока нет: помощник ещё никто не запускал."}</div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full text-left text-[12.5px]">
            <thead className="bg-bg-subtle text-[11px] uppercase tracking-[0.05em] text-fg-faint">
              <tr>
                <th className="px-3 py-2 font-medium">Когда</th>
                <th className="px-3 py-2 font-medium">Кто</th>
                <th className="px-3 py-2 font-medium">Компьютер</th>
                <th className="px-3 py-2 font-medium">Что</th>
                <th className="px-3 py-2 font-medium">Подробно</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {events.map((e) => (
                <tr key={e.id} className={e.ok ? "" : "bg-err-bg"}>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">{when(e.at)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{e.user || <span className="text-fg-faint">до входа</span>}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">
                    {e.computer}
                    {/^\d+\.\d+\.\d+$/.test(e.script) && <span className="text-fg-faint"> · помощник {e.script}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className={e.ok ? "text-fg" : "font-semibold text-err"}>
                      {HELPER_CODE[e.code] ?? e.code}
                      {e.ok ? "" : " — не вышло"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-fg-muted">
                    {e.text}
                    {typeof e.data.line === "number" && <span className="text-fg-faint"> · строка {e.data.line}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Вкладка «Помощник»: программа для Windows, которая находит браузеры Chrome сотрудника, помогает
 * поставить в них расширение и дальше обновляет его сама. Как скачать и поставить, что она делает
 * на компьютере, и её журнал. Спека `docs/specs/2026-10-05-ext-helper-design.md`.
 */
function HelperTab() {
  const [copied, setCopied] = useState<"" | "ok" | "manual">("");
  const command = extHelperCommand();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied("ok");
    } catch {
      setCopied("manual"); // буфер недоступен — команду показываем, копируют руками
    }
  };
  return (
    <div className="flex flex-col gap-8">
      <div className="flex max-w-[860px] flex-col gap-5">
        <p className="text-[13.5px] text-fg-muted">
          Помощник — небольшая программа для Windows. Она находит все ваши браузеры Chrome (каждый ярлык — отдельный
          браузер), показывает, где расширение уже стоит, подсказывает, что нажать, чтобы поставить его в остальные, и
          дальше сама обновляет расширение — скачивать и распаковывать архив больше не нужно.
        </p>
        <ol className="flex flex-col gap-5">
          <Step n={1} title="Скачайте помощника">
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <a
                href={extHelperFile()}
                download
                className="inline-flex w-fit items-center gap-2 rounded-[var(--radius-ui)] bg-accent px-3.5 py-2 text-[13.5px] font-semibold text-white hover:opacity-90"
                data-annot="скачать помощника"
              >
                <Download size={15} />
                Скачать prokat-helper.cmd
              </a>
              <span className="text-[12.5px] text-fg-faint">один файл, ставить можно на любой свой компьютер с Windows 10 или 11</span>
            </div>
          </Step>
          <Step n={2} title="Запустите скачанный файл">
            Откройте папку «Загрузки» и дважды щёлкните по <span className={code}>prokat-helper.cmd</span>. Windows может
            спросить, запускать ли файл из интернета: нажмите «Подробнее» → «Выполнить в любом случае» (или «Запустить»).
            На несколько секунд появится чёрное окно — это установка, закрывать его не нужно.
            <div className="mt-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2">
              <div className="text-[12.5px] text-fg">Если файл не запускается — то же самое командой:</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex w-fit items-center gap-2 rounded-[var(--radius-ui)] border border-border bg-bg px-3 py-1.5 text-[13px] font-medium hover:bg-bg-hover"
                  data-annot="скопировать команду помощника"
                >
                  {copied === "ok" ? <Check size={14} /> : <Copy size={14} />}
                  {copied === "ok" ? "Скопировано" : "Скопировать команду"}
                </button>
                <span className="text-[12.5px] text-fg-muted">затем Win+R → Ctrl+V → Enter</span>
              </div>
              {copied === "manual" && (
                <input
                  readOnly
                  value={command}
                  onFocus={(e) => e.currentTarget.select()}
                  className="mt-2 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5 font-mono text-[12px]"
                  aria-label="Команда установки помощника"
                />
              )}
            </div>
          </Step>
          <Step n={3} title="Войдите в помощник своим паролем">
            Откроется окно «Прокат — расширение Chrome». Введите пароль, с которым входите в этот кабинет, и нажмите
            «Войти». Пароль на компьютере не сохраняется — он нужен один раз, чтобы в кабинете было видно, чей это
            компьютер. После входа помощник сам скачает расширение.
            <Shot src={shotHelperLogin} alt="Окно входа в помощник" width={560} />
          </Step>
          <Step n={4} title="Нажмите «Поставить» у каждого браузера, где нужна панель">
            В окне — список ваших браузеров Chrome. «Поставить» открывает страницу расширений нужного браузера, а в окне
            помощника появляются три шага: включить «Режим разработчика» → «Загрузить распакованное расширение» → в окне
            выбора папки нажать Ctrl+V и «Выбор папки» (путь помощник уже скопировал). Строка в окне становится зелёной —
            готово.
            <Shot src={shotHelperWindow} alt="Окно помощника со списком браузеров" width={560} />
          </Step>
          <Step n={5} title="Дальше ничего делать не нужно — смотрите на цветную строку">
            Ставится расширение один раз в каждый браузер. Помощник работает в фоне, пока включён компьютер: раз в минуту
            спрашивает кабинет и сам скачивает новую версию, расширение подхватывает её без вас; вкладку с мессенджером, если она открыта, попросит обновить кнопка сверху. На
            рабочем столе остаётся ярлык «Прокат — расширение» — им окно помощника открывается снова. Сверху в окне
            одна цветная строка говорит, всё ли в порядке:
            <ul className="mt-1.5 flex flex-col gap-1">
              <li>
                <span className="font-semibold text-ok">зелёная</span> «Всё обновлено… Нажимать ничего не нужно» — всё
                хорошо, кнопка справа серая «Проверить обновления»;
              </li>
              <li>
                <span className="font-semibold text-err">оранжевая</span> «Вышла новая версия расширения — нажмите
                «Обновить сейчас»» — кнопка справа тоже оранжевая, нажмите её; так же оранжевым помощник пишет, если в
                каком-то браузере расширение отключено или стоит из старой папки, и называет кнопку в строке браузера;
              </li>
              <li>жёлтая «Обновляется: браузер подхватит версию в течение минуты» — подождите, нажимать не нужно;</li>
              <li>синяя «Расширение пока не стоит ни в одном браузере» — нажмите «Поставить».</li>
            </ul>
            <Shot src={shotHelperOk} alt="Зелёная строка: всё обновлено" width={560} />
            <Shot src={shotHelperUpdate} alt="Оранжевая строка и кнопка «Обновить сейчас»" width={560} />
          </Step>
        </ol>
        <div className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[12.5px] text-fg-muted">
          <div className="text-[13px] font-semibold text-fg">Что помощник делает на компьютере</div>
          <div>
            Хранит расширение и себя в папке <span className={code}>%LOCALAPPDATA%\ProkatExt</span>. Работает в
            фоне без окон: запускается сам при входе в Windows, раз в минуту спрашивает кабинет о новой версии; задача
            «ProkatExt» в Планировщике Windows раз в пять минут проверяет, что он запущен.
          </div>
          <div>
            Сообщает в кабинет: в каких браузерах расширение стоит (имя ярлыка и версия — без адресов сайтов и содержимого
            браузера) и журнал своей работы — что обновил и что не получилось. Настройки Chrome он только читает.
          </div>
          <div>
            Права администратора не нужны. Удалить: Win+R →{" "}
            <span className={code}>{'powershell -ExecutionPolicy Bypass -File "%LOCALAPPDATA%\\ProkatExt\\helper.ps1" remove'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const LOGS = [
  { id: "actions", label: "Журнал действий", hint: "что сотрудники делали в окне расширения" },
  { id: "helper", label: "Журнал помощника", hint: "что помощник делал на их компьютерах" },
  { id: "bridge", label: "Мост", hint: "служебные браузеры: ответы из «Чатов» по шагам, ошибки расширения" },
] as const;

/**
 * Вкладка «Журнал» — только по праву «Видеть журналы расширения» (владелец, 05.10.26): журналы
 * действий и помощника и журнал моста, между ними переключатель (`&log=helper`, `&log=bridge`).
 */
function Journals({ users, log, setLog }: { users: ExtStatus["users"]; log: string; setLog: (id: string) => void }) {
  const current = LOGS.find((l) => l.id === log)?.id ?? "actions";
  return (
    <div className="flex flex-col gap-4">
      <div role="tablist" aria-label="Какой журнал" className="flex w-fit flex-wrap gap-1 rounded-[var(--radius-ui)] border border-border p-1">
        {LOGS.map((l) => (
          <button
            key={l.id}
            type="button"
            role="tab"
            aria-selected={current === l.id}
            title={l.hint}
            onClick={() => setLog(l.id)}
            className={`rounded-[6px] px-3 py-1.5 text-[13px] font-medium ${
              current === l.id ? "bg-accent-dim text-accent-text" : "text-fg-muted hover:bg-bg-hover hover:text-fg"
            }`}
            data-annot={`журнал «${l.label}»`}
          >
            {l.label}
          </button>
        ))}
      </div>
      {current === "helper" ? <HelperJournal /> : current === "bridge" ? <BridgeJournal /> : <Journal users={users} />}
    </div>
  );
}

function Install({ status, toHelper }: { status: ExtStatus; toHelper: () => void }) {
  return (
    <div className="flex flex-col gap-5">
      <HelperCallout go={toHelper} />
      <h3 className="text-[13px] font-semibold text-fg-muted">Без помощника — вручную</h3>
      <ManualInstall status={status} />
    </div>
  );
}

function ManualInstall({ status }: { status: ExtStatus }) {
  return (
    <ol className="flex max-w-[860px] flex-col gap-5">
      <Step n={1} title="Скачайте архив расширения">
        <div className="mt-2">
          <DownloadButton status={status} />
        </div>
      </Step>
      <Step n={2} title="Распакуйте его в постоянную папку">
        Правой кнопкой по <span className={code}>prokat-ext.zip</span> → «Извлечь всё…» → в поле пути впишите{" "}
        <span className={code}>{FOLDER}</span> → «Извлечь». Папку потом не переносите и не удаляйте: Chrome читает
        расширение прямо из неё.
      </Step>
      <Step n={3} title="Откройте страницу расширений Chrome и включите режим разработчика">
        В адресной строке наберите <span className={code}>chrome://extensions</span> и нажмите Enter. Справа вверху
        включите переключатель «Режим разработчика» (Developer mode).
        <Shot src={shotDevMode} alt="Переключатель «Режим разработчика» справа вверху" mark={marks.devMode} />
      </Step>
      <Step n={4} title="Нажмите «Загрузить распакованное расширение» и выберите папку">
        Кнопка появится слева вверху (Load unpacked). В окне выбора укажите папку <span className={code}>{FOLDER}</span>{" "}
        — саму папку, в которой лежит файл <span className={code}>manifest.json</span>.
        <Shot src={shotLoad} alt="Кнопка «Загрузить распакованное расширение»" mark={marks.load} />
      </Step>
      <Step n={5} title="Проверьте, что расширение появилось, и закрепите значок">
        В списке появится карточка «Прокат — панель менеджера». Нажмите значок-пазл справа от адресной строки и булавку
        напротив «Прокат» — значок с номером версии будет всегда на виду.
        <Shot src={shotCard} alt="Карточка расширения «Прокат — панель менеджера»" />
      </Step>
      <Step n={6} title="Войдите в кабинет и откройте мессенджер">
        Вы уже вошли в prokat в этом браузере — пароль расширение не спросит. Откройте (или обновите клавишей F5) вкладку
        Telegram Web, MAX или WhatsApp Web: в правом нижнем углу появится панель «Прокат», а эта страница покажет вкладку
        на вкладке «Кто онлайн».
      </Step>
    </ol>
  );
}

function Update({ status }: { status: ExtStatus }) {
  const latest = status.release?.version ?? "…";
  return (
    <ol className="flex max-w-[860px] flex-col gap-5">
      <Step n={1} title="Значок расширения стал красным — вышла новая версия">
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="flex items-center gap-2.5">
            <Badge tone="ok" version="11.0" /> зелёный — версия свежая
          </span>
          <span className="flex items-center gap-2.5">
            <Badge tone="err" version="10.0" /> красный — пора обновить
          </span>
        </div>
        <div className="mt-2">
          Цифры на значке — номер версии (0.11.0 пишется «11.0»). Нажатие на значок открывает эту страницу.
        </div>
      </Step>
      <Step n={2} title={`Скачайте свежий архив — версия ${latest}`}>
        <div className="mt-2">
          <DownloadButton status={status} />
        </div>
      </Step>
      <Step n={3} title="Распакуйте его в ту же папку с заменой файлов">
        «Извлечь всё…» → тот же путь <span className={code}>{FOLDER}</span> → на вопрос о совпадающих файлах ответьте
        «Заменить». Удалять расширение из Chrome и ставить заново не нужно.
      </Step>
      <Step n={4} title="Перезагрузите расширение">
        Откройте <span className={code}>chrome://extensions</span> и нажмите круглую стрелку на карточке «Прокат — панель
        менеджера». Номер версии на карточке сменится на {latest}.
        <Shot src={shotCard} alt="Кнопка перезагрузки на карточке расширения" mark={marks.reload} />
      </Step>
      <Step n={5} title="Обновите вкладки мессенджеров">
        На вкладках Telegram Web, MAX и WhatsApp Web нажмите F5 — иначе на них останется старая панель. Значок расширения
        станет зелёным, а эта страница покажет новую версию.
      </Step>
    </ol>
  );
}

/** Что умеет панель — на снимках настоящей переписки в WhatsApp Web. */
function How() {
  return (
    <ol className="flex max-w-[860px] flex-col gap-5">
      <Step n={1} title="Значок на панели Chrome — расширение на месте">
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="flex items-center gap-2.5">
            <Badge tone="ok" version="12.0" /> зелёный — стоит и свежее
          </span>
          <span className="flex items-center gap-2.5">
            <Badge tone="err" version="11.0" /> красный — пора обновить
          </span>
        </div>
        <div className="mt-2">
          Значка не видно — нажмите значок-пазл справа от адресной строки и булавку напротив «Прокат».
        </div>
      </Step>
      <Step n={2} title="Панель появляется в открытом чате">
        Откройте переписку с клиентом в WhatsApp Web, Telegram Web или MAX — справа внизу появится панель. В шапке —
        номер клиента, ниже — его сделка (номер открывает карточку), стадия и дата следующей обработки: меняются прямо
        здесь. Панель двигается за шапку, размер — за любой край или угол, «—» сворачивает её, «□» разворачивает на
        весь экран.
        <Shot src={shotChat} alt="Переписка с клиентом в WhatsApp Web и панель расширения справа" />
      </Step>
      <Step n={3} title="Клиент не может определиться со временем — отправьте ссылку">
        Клиент хочет на примерку, но не знает, какой день и час ему удобны. Не дожимайте: откройте его чат в WhatsApp
        Web, Telegram Web или MAX и нажмите в панели «Ссылка на запись». Когда клиент определится, он либо напишет вам
        в чат, либо сам выберет время по ссылке.
        <Shot src={shotPanel} alt="Кнопка «Ссылка на запись» в панели" mark={marks.zapis} width={466} />
      </Step>
      <Step n={4} title="Текст со ссылкой встал в поле — проверьте и отправьте">
        Панель вставляет в поле сообщения текст «Выбрать удобный день и время примерки можно самостоятельно по ссылке —
        там же видно, когда в салоне свободнее:» и персональную ссылку клиента, а под кнопкой показывает зелёную
        подсказку «Текст со ссылкой — в поле сообщения. Проверьте и отправьте.». Текст можно поправить, но отправляете
        вы сами: панель ничего не отправляет. В разделе «Чаты» та же кнопка называется «Отправить ссылку на запись».
        <div className="mt-2">
          Хотите сразу предложить время — нажмите рядом «Время записи». Панель покажет загрузку салона по часам на 14
          дней и сама отметит самые свободные часы трёх ближайших дней; «Вставить в поле» добавит их к ссылке:
          «сегодня, 5 октября — с 15:00 до 17:00». Клиент назвал свои дни — нажмите «снять все» и поставьте галочки
          под нужными датами: у каждого дня подставятся его свободные часы, их можно поправить. Красный час («мест
          нет») отметить нельзя, «пик» в выходные с 12 до 16 — только осознанно: по регламенту туда не записываем.
        </div>
        <Shot
          src={shotField}
          alt="Зелёная подсказка в панели, текст со ссылкой в поле сообщения и кнопка отправки"
          mark={[marks.zapisDone, marks.zapisField, marks.zapisSend]}
        />
      </Step>
      <Step n={5} title="Ссылка персональная и записана на вас">
        Ссылка создаётся для этого клиента и для вас: запись клиента по ней засчитывается тому, кто её создал. Поэтому
        ссылку всегда создавайте кнопкой — не копируйте её из чужого чата и не отправляйте клиенту просто адрес
        my.uni-renter.ru.
      </Step>
      <Step n={6} title="Что видит клиент">
        По ссылке клиент попадает в личный кабинет, где город уже подставлен из сделки: выбирает день в календаре (точка
        под числом — загрузка салона) и час (зелёный — свободно, оранжевый — много записей, красный — очень много),
        вводит телефон и нажимает «Записаться». Его сделка сама встаёт в «Ждем клиента» на это время. Дальше клиент
        видит экран «Вы записаны»: дату, время и номер заявки, кнопку «Изменить время» (перенести может сам, отменить —
        только через вас), ботов MAX и Telegram для подтверждения и напоминания, адрес салона с ориентиром, график,
        путь от метро и парковки.
        <div className="flex flex-wrap items-start gap-x-4">
          <Shot src={shotMyCity} alt="Кабинет клиента: календарь с загрузкой салона и часы" mark={[marks.myDay, marks.myHours]} width={300} />
          <Shot src={shotMyBooked} alt="Кабинет клиента: экран «Вы записаны»" mark={marks.myBooked} width={300} />
        </div>
      </Step>
      <Step n={7} title="Похожие платья по фото клиента">
        Клиент прислал фото платья, которое ему нравится, — задержите на нём курсор (или откройте фото). Панель сама
        покажет похожие платья, которые есть в салоне: сходство в процентах, серия, цена проката. Город выбирается над
        плиткой. Поиск по наведению выключается галочкой «Искать при наведении курсора на фото».
        <Shot src={shotDress} alt="Фото клиента в переписке и похожие платья в панели" mark={[marks.photo, marks.toChat]} />
      </Step>
      <Step n={8} title="«Фильтр» сужает подборку">
        Тип платья, размер, силуэт, цвет, длина — применяется сразу, «Готово» закрывает фильтр. Под фильтр попадают и
        фото в чат, и подборка по ссылке.
        <Shot src={shotFilter} alt="Раскрытый фильтр подборки" mark={marks.filter} width={466} />
      </Step>
      <Step n={9} title="Отправьте подборку клиенту">
        «В чат: 5 лучших + ссылка» кладёт в окно отправки фото пяти самых похожих платьев и текст со ссылкой на
        страницу подборки; отметили платья кружком — уйдут отмеченные. «Только ссылка» — одна ссылка на подборку, без
        фото. Отправляете вы сами. Подборка тоже записана на вас.
      </Step>
    </ol>
  );
}

/** Что панель показывала в момент сообщения (её событие `view`). */
const VIEW: Record<string, string> = {
  "check-login": "«Проверяю вход…»",
  login: "форму входа",
  "prokat-error": "«prokat не ответил»",
  "platform-loading": "«площадка ещё загружается…»",
  "no-dialog": "«Откройте диалог с клиентом»",
  "not-person": "«Это не личный диалог с человеком»",
  dialog: "диалог клиента",
};
const clock = (ms: unknown) => (typeof ms === "number" ? new Date(ms).toLocaleTimeString("ru-RU") : "");
const field = (o: unknown, key: string): unknown => (o && typeof o === "object" ? (o as Record<string, unknown>)[key] : undefined);

function BugEvent({ e }: { e: ExtBugEvent }) {
  const { t, t2, k, n, src, ...rest } = e;
  return (
    <div className="flex gap-2 font-mono text-[12px] leading-snug">
      <span className="flex-none tabular-nums text-fg-faint">{clock(t)}</span>
      <span className="flex-none font-semibold">
        {src === "panel" ? "панель " : ""}
        {k ?? "?"}
        {n ? ` ×${n} до ${clock(t2)}` : ""}
      </span>
      <span className="min-w-0 break-all text-fg-muted">{Object.keys(rest).length ? JSON.stringify(rest) : ""}</span>
    </div>
  );
}

function BugCard({ bug }: { bug: ExtBug }) {
  const view = String(field(field(bug.env, "panel"), "view") ?? "");
  const url = String(field(bug.env, "url") ?? "");
  return (
    <article className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border p-4" data-annot={`баг ${bug.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 text-[12.5px] text-fg-muted">
        <span className="font-mono font-semibold text-fg">№ {bug.id}</span>
        <span>{when(bug.date)}</span>
        <span className="font-medium text-fg">{bug.user}</span>
        <span>{bug.platform}</span>
        <span>версия {bug.version}</span>
      </div>
      <div className="whitespace-pre-wrap text-[13.5px]">{bug.text || "без описания"}</div>
      <div className="text-[12.5px] text-fg-muted">
        {bug.peer_name || bug.peer_id ? `Диалог: ${bug.peer_name || bug.peer_id}${bug.phone ? ` · ${phone(bug.phone)}` : ""}` : "Диалог панели не был известен"}
        {view ? ` · панель показывала ${VIEW[view] ?? view}` : ""}
        {url ? ` · ${url}` : ""}
      </div>
      <details>
        <summary className="cursor-pointer text-[12.5px] text-accent-text">Что делали до нажатия · {bug.events.length} событий</summary>
        <div className="mt-2 flex flex-col gap-0.5 overflow-x-auto">
          {bug.events.map((e, i) => (
            <BugEvent key={i} e={e} />
          ))}
        </div>
      </details>
      <details>
        <summary className="cursor-pointer text-[12.5px] text-accent-text">Окружение</summary>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[12px] text-fg-muted">{JSON.stringify(bug.env, null, 1)}</pre>
      </details>
    </article>
  );
}

function Bugs() {
  const q = useQuery({ queryKey: ["ext-bugs"], queryFn: loadExtBugs, refetchInterval: 30_000 });
  return (
    <div className="flex max-w-[860px] flex-col gap-3">
      <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4 text-[13.5px]" data-annot="как сообщить о баге">
        <div className="font-semibold">Панель ведёт себя не так — нажмите значок жука в её шапке</div>
        <div className="mt-1 text-fg-muted">
          Он слева от «—». Опишите в двух словах, что не работает, и нажмите «Отправить». Вместе с описанием уйдут
          последние действия на вкладке — куда нажимали, какой диалог был открыт, что ответили панель и prokat. Текст,
          который вы набираете клиенту, не передаётся.
        </div>
      </div>
      {q.isPending && <div className="text-[13px] text-fg-muted">Загрузка…</div>}
      {q.isError && <div className="text-[13px] text-err">{q.error.message}</div>}
      {q.data && (
        <>
          <div className="text-[12.5px] text-fg-faint">{q.data.all ? "Сообщения всех сотрудников" : "Ваши сообщения"}, новые сверху</div>
          {q.data.bugs.length === 0 && <div className="text-[13px] text-fg-muted">Сообщений пока нет.</div>}
          {q.data.bugs.map((b) => (
            <BugCard key={b.id} bug={b} />
          ))}
        </>
      )}
    </div>
  );
}

const TABS = [
  { id: "helper", label: "Помощник", hint: "поставит и обновит сам" },
  { id: "who", label: "Кто онлайн", hint: "у кого расширение работает" },
  { id: "install", label: "Установка", hint: "вручную" },
  { id: "update", label: "Обновление", hint: "значок стал красным" },
  { id: "how", label: "Как работает", hint: "что умеет панель" },
  { id: "bugs", label: "Баги", hint: "что-то не работает" },
  { id: "events", label: "Журнал", hint: "действия в окне и помощник" },
] as const;
type Tab = (typeof TABS)[number]["id"];

/** Разрез вкладки «Кто онлайн» (`&by=`). */
const BY = [
  { id: "people", label: "По сотрудникам" },
  { id: "phones", label: "По номерам" },
] as const;

export function ExtensionPage() {
  const [params, setParams] = useSearchParams();
  // «Журнал» — только по праву: без него вкладки нет, а ссылка на неё открывает «Помощника».
  const canJournal = useCan(EXT_JOURNAL);
  const tabs = TABS.filter((t) => t.id !== "events" || canJournal);
  const tab: Tab = tabs.find((t) => t.id === params.get("tab"))?.id ?? "helper";
  const here = useHere();
  const q = useQuery({ queryKey: ["ext-status"], queryFn: loadExtStatus, refetchInterval: 15_000 });
  const status = q.data;
  const latest = status?.release?.version ?? null;
  const mine = status?.users.flatMap((u) => u.installs).find((i) => i.install === here?.install);
  // Свежесть версии этого браузера знает сервер (по отметке); отметки ещё нет — сравним сами.
  const fresh = mine ? mine.fresh : !latest || here?.version === latest;
  const online = status?.users.filter((u) => u.active).length ?? 0;
  const by = BY.find((b) => b.id === params.get("by"))?.id ?? "people";

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
      <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Расширение Chrome «Прокат — панель менеджера»</h1>

      <div className="grid gap-3 lg:grid-cols-2" data-annot="состояние расширения">
        <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4">
          <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-fg-faint">В этом браузере</div>
          {here ? (
            <div className="flex items-center gap-3">
              <Badge tone={fresh ? "ok" : "err"} version={here.version.startsWith("0.") ? here.version.slice(2) : here.version} />
              <div className="text-[13.5px]">
                <div className="font-semibold">
                  Расширение установлено · версия {here.version}
                  {mine ? (mine.active ? " · активно" : " · неактивно") : ""}
                </div>
                <div className={fresh ? "text-ok" : "text-err"}>
                  {fresh ? "Версия свежая." : `Вышла версия ${latest} — обновите по вкладке «Обновление».`}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 text-[13.5px]">
              <Dot tone="off" />
              <div>
                <div className="font-semibold">Расширение не найдено</div>
                <div className="text-fg-muted">Поставьте его по инструкции ниже и обновите эту страницу.</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4">
          <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-fg-faint">Последняя сборка</div>
          {status ? (
            <>
              <DownloadButton status={status} />
              {status.release?.version && (
                <div className="text-[12.5px] text-fg-muted">
                  Выпущена {when(status.release.date)} · {size(status.release.size)}. Здесь всегда лежит последняя.
                </div>
              )}
            </>
          ) : (
            <div className="text-[13px] text-fg-muted">{q.isError ? q.error.message : "Загрузка…"}</div>
          )}
        </div>
      </div>

      <div role="tablist" aria-label="Разделы" className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setParams(t.id === "helper" ? {} : { tab: t.id }, { replace: true })}
            className={`flex items-baseline gap-2 rounded-[var(--radius-card)] border px-4 py-2 ${
              tab === t.id ? "border-accent bg-accent-dim text-accent-text" : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
            }`}
            data-annot={`вкладка «${t.label}»`}
          >
            <span className="text-[15px] font-semibold">{t.label}</span>
            <span className="text-[12px] opacity-80">{t.id === "who" && status ? `сейчас работают: ${online}` : t.hint}</span>
          </button>
        ))}
      </div>
      {tab === "helper" ? (
        <HelperTab />
      ) : tab === "who" ? (
        // Отдельной вкладкой (владелец, 05.10.26): над инструкциями список сотрудников уводил их вниз.
        <section className="rounded-[var(--radius-card)] border border-border p-4" data-annot="кто сейчас работает">
          <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-[14px] font-semibold">Кто сейчас работает</h2>
            <span className="text-[12px] text-fg-faint">обновляется само · номер и его название — из справочника номеров CRM</span>
          </div>
          <div role="tablist" aria-label="Разрез" className="mb-3 flex w-fit gap-1 rounded-[var(--radius-ui)] border border-border p-1" data-annot="разрез">
            {BY.map((b) => (
              <button
                key={b.id}
                type="button"
                role="tab"
                aria-selected={by === b.id}
                onClick={() => setParams(b.id === "people" ? { tab: "who" } : { tab: "who", by: b.id }, { replace: true })}
                className={`rounded-[6px] px-3 py-1.5 text-[13px] font-medium ${
                  by === b.id ? "bg-accent-dim text-accent-text" : "text-fg-muted hover:bg-bg-hover hover:text-fg"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
          {!status ? (
            <div className="text-[13px] text-fg-muted">{q.isError ? q.error.message : "Загрузка…"}</div>
          ) : by === "phones" ? (
            <Phones
              status={status}
              group={params.get("group") ?? ""}
              off={params.get("off") === "1"}
              can={canJournal}
              set={(patch) =>
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
                    return next;
                  },
                  { replace: true },
                )
              }
            />
          ) : (
            <>
              <Helpers status={status} />
              <Versions status={status} />
              <Who status={status} here={here} />
            </>
          )}
        </section>
      ) : tab === "how" ? (
        <How />
      ) : tab === "bugs" ? (
        <Bugs />
      ) : tab === "events" ? (
        <Journals
          users={status?.users ?? []}
          log={params.get("log") ?? ""}
          setLog={(id) => setParams(id === "actions" ? { tab: "events" } : { tab: "events", log: id }, { replace: true })}
        />
      ) : (
        status && (tab === "install" ? <Install status={status} toHelper={() => setParams({}, { replace: true })} /> : <Update status={status} />)
      )}
    </main>
  );
}
